import { ArrowRight, CirclePause, CirclePlay, LoaderCircle, RefreshCw, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import type { BotMode } from "../../shared/contracts";
import { DefinitionRow, Metric, SignalList } from "../components/data-display";
import { LiveActivationDialog } from "../components/confirmations";
import { DashboardLoading, EmptyState, ErrorState, PageHeader } from "../components/page-state";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useAuth } from "../hooks/use-auth";
import { useDashboard } from "../hooks/use-dashboard";
import { api, getErrorMessage } from "../lib/api";
import { formatDateTime, formatEth, formatPercent, formatSignedUsd, formatUsd, pnlTone } from "../lib/format";
import { getTradingReadiness } from "../lib/readiness";
import { cn } from "../lib/utils";

const statusCopy = {
  stopped: "Automation stopped",
  running: "Scanning ETH/USD",
  paused: "Hyper Profits is paused",
  attention: "Action needed",
};

export function HomePage() {
  const { session } = useAuth();
  const { dashboard, loading, error, refresh, replaceDashboard } = useDashboard();
  const [busy, setBusy] = useState(false);
  const [liveDialogOpen, setLiveDialogOpen] = useState(false);
  const [startAfterActivation, setStartAfterActivation] = useState(false);

  if (loading && !dashboard) return <DashboardLoading />;
  if (!dashboard) return <ErrorState message={error ?? "No dashboard snapshot was returned."} onRetry={() => void refresh()} />;

  const { bot, connection, market, fees, portfolio, position, signals, recentEvents } = dashboard;
  const csrf = session.csrfToken;
  const readiness = getTradingReadiness(dashboard);
  const controlRequiresReadiness = bot.status === "stopped" || bot.status === "paused";
  const controlDisabled = busy || (controlRequiresReadiness && !readiness.ready);
  const controlLabel = bot.status === "running"
    ? "Pause bot"
    : bot.status === "paused"
      ? "Resume bot"
      : bot.status === "attention"
        ? "Stop bot"
        : "Start bot";

  async function setMode(mode: BotMode, acknowledgeRisk?: boolean) {
    if (!csrf) throw new Error("Your secure session needs to be refreshed.");
    const snapshot = await api.setMode(mode, csrf, acknowledgeRisk);
    replaceDashboard(snapshot);
    return snapshot;
  }

  async function activateLive() {
    try {
      setBusy(true);
      const snapshot = await setMode("live", true);
      if (startAfterActivation && snapshot.bot.status !== "running") {
        const started = await api.botControl("start", csrf!);
        replaceDashboard(started);
        toast.success("Live ETH/USD automation started.");
      } else {
        toast.success("Live trading mode enabled.");
      }
      setStartAfterActivation(false);
    } finally {
      setBusy(false);
    }
  }

  async function chooseMode(mode: BotMode) {
    if (mode === bot.mode) return;
    if (mode === "live") {
      setStartAfterActivation(false);
      setLiveDialogOpen(true);
      return;
    }
    try {
      setBusy(true);
      await setMode("paper");
      toast.success("Paper trading mode enabled.");
    } catch (caught) {
      toast.error(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function controlBot() {
    if (!csrf) {
      toast.error("Your secure session needs to be refreshed.");
      return;
    }
    if (bot.status === "stopped" && bot.mode === "live" && !bot.liveAcknowledged) {
      setStartAfterActivation(true);
      setLiveDialogOpen(true);
      return;
    }
    const action = bot.status === "running"
      ? "pause"
      : bot.status === "paused"
        ? "resume"
        : bot.status === "attention"
          ? "stop"
          : "start";
    try {
      setBusy(true);
      const snapshot = await api.botControl(action, csrf);
      replaceDashboard(snapshot);
      toast.success(
        action === "pause"
          ? "Automation paused."
          : action === "stop"
            ? "Automation stopped."
            : "Automation started.",
      );
    } catch (caught) {
      toast.error(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="CONTROL ROOM"
        title="ETH/USD automation, clearly explained."
        description="One calm view of the bot, the account, and the fee-adjusted decision being made now."
        actions={
          <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={busy}>
            <RefreshCw data-icon="inline-start" /> Refresh
          </Button>
        }
      />

      <section className="grid overflow-hidden rounded-[22px] bg-rail text-white lg:grid-cols-[minmax(0,1.45fr)_minmax(18rem,0.55fr)]" aria-labelledby="bot-status-heading">
        <div className="flex min-h-[270px] flex-col justify-between gap-8 p-6 sm:p-8">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={bot.status === "running" ? "signal" : bot.status === "attention" ? "warning" : "rail"}>
                <span className={cn("size-1.5 rounded-full", bot.status === "running" ? "bg-current" : "bg-rail-muted")} />
                {statusCopy[bot.status]}
              </Badge>
              <Badge variant="rail">{bot.strategyName}</Badge>
            </div>
            <h2 id="bot-status-heading" className="font-display mt-5 max-w-2xl text-3xl font-bold leading-tight tracking-[-0.04em] sm:text-4xl">
              {bot.message}
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-6 text-rail-muted">
              {position
                ? "An ETH/USD position is being managed under the strategy rules shown in Live Trade."
                : "No order is placed until the setup remains worthwhile after authenticated Kraken fees and expected slippage."}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              variant="signal"
              size="lg"
              onClick={() => void controlBot()}
              disabled={controlDisabled}
              aria-describedby={!readiness.ready && controlRequiresReadiness ? "trading-readiness" : undefined}
            >
              {busy ? <LoaderCircle data-icon="inline-start" className="animate-spin" /> : bot.status === "running" || bot.status === "attention" ? <CirclePause data-icon="inline-start" /> : <CirclePlay data-icon="inline-start" />}
              {controlLabel}
            </Button>
            <Button variant="rail" size="lg" asChild>
              <Link to="/trade">Open Live Trade <ArrowRight data-icon="inline-end" /></Link>
            </Button>
          </div>
        </div>
        <div className="border-t border-white/10 bg-white/[0.035] p-6 sm:p-8 lg:border-l lg:border-t-0">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-bold text-rail-muted">TRADING MODE</p>
            <ShieldCheck className="size-4 text-signal" aria-hidden="true" />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 rounded-xl bg-black/20 p-1.5" role="group" aria-label="Trading mode">
            {(["paper", "live"] as BotMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                className={cn(
                  "min-h-11 rounded-lg px-3 text-sm font-semibold capitalize text-rail-muted transition-colors hover:text-white",
                  bot.mode === mode && "bg-white text-rail",
                )}
                aria-pressed={bot.mode === mode}
                aria-describedby={mode === "live" && !readiness.ready ? "trading-readiness" : undefined}
                onClick={() => void chooseMode(mode)}
                disabled={busy || (mode === "live" && bot.mode !== "live" && !readiness.ready)}
              >
                {mode}
              </button>
            ))}
          </div>
          <dl className="mt-6">
            <div className="flex items-center justify-between gap-3 border-b border-white/10 py-3 text-sm">
              <dt className="text-rail-muted">Kraken</dt>
              <dd className="m-0 font-semibold">{connection.connected ? "Connected" : "Disconnected"}</dd>
            </div>
            <div className="flex items-center justify-between gap-3 border-b border-white/10 py-3 text-sm">
              <dt className="text-rail-muted">Market data</dt>
              <dd className="m-0 font-semibold">{market.stale ? "Stale" : market.systemStatus}</dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-3 text-sm">
              <dt className="text-rail-muted">Fee source</dt>
              <dd className="m-0 font-semibold">{fees.source === "authenticated" ? "Account verified" : "Unavailable"}</dd>
            </div>
          </dl>
          <div id="trading-readiness" className={cn(
            "mt-4 rounded-xl border px-3.5 py-3 text-xs leading-5",
            readiness.ready
              ? "border-signal/25 bg-signal/8 text-white"
              : "border-coral/25 bg-coral/8 text-white",
          )}>
            <p className="font-semibold">{readiness.ready ? "Trading readiness passed" : "Trading is not ready"}</p>
            {readiness.ready ? (
              <p className="mt-1 text-rail-muted">Credentials, permissions, fees, and market freshness are verified.</p>
            ) : (
              <>
                <ul className="m-0 mt-2 grid list-disc gap-1 pl-4 text-rail-muted">
                  {readiness.issues.map((issue) => <li key={issue}>{issue}</li>)}
                </ul>
                <Link to="/connection" className="mt-2 inline-flex min-h-11 items-center font-semibold text-white underline decoration-white/35 underline-offset-4">Review readiness checks</Link>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-x-4 gap-y-7 border-y border-border py-6 sm:grid-cols-4 sm:gap-x-5" aria-label="Account summary">
        <Metric label="Portfolio value" value={formatUsd(portfolio.totalUsd)} detail="Kraken spot account" />
        <Metric label="Today, after fees" value={formatSignedUsd(portfolio.todayNetPnlUsd)} tone={pnlTone(portfolio.todayNetPnlUsd)} detail="Realized net P/L" />
        <Metric label="Kraken fees today" value={formatUsd(portfolio.todayFeesUsd)} detail={fees.makerPercent == null ? "Fee rate unavailable" : `${formatPercent(fees.makerPercent)} maker`} />
        <Metric label="ETH/USD" value={formatUsd(market.price)} detail={market.change24hPercent == null ? "24h change unavailable" : `${formatPercent(market.change24hPercent, true)} over 24h`} tone={pnlTone(market.change24hPercent)} />
      </section>

      <div className="grid gap-8 xl:grid-cols-[minmax(0,1.25fr)_minmax(20rem,0.75fr)]">
        <section aria-labelledby="position-heading">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold text-muted-foreground">CURRENT EXPOSURE</p>
              <h2 id="position-heading" className="font-display mt-1 text-xl font-bold tracking-[-0.03em]">{position ? "Open ETH/USD position" : "No open position"}</h2>
            </div>
            {position && <Badge variant={position.mode === "live" ? "outline" : "secondary"}>{position.mode}</Badge>}
          </div>
          {position ? (
            <div className="grid rounded-2xl border border-border bg-card px-5 py-2 sm:grid-cols-2 sm:gap-x-8 sm:px-6">
              <dl>
                <DefinitionRow label="Quantity" value={formatEth(position.quantityEth)} />
                <DefinitionRow label="Entry" value={formatUsd(position.entryPrice)} />
                <DefinitionRow label="Current" value={formatUsd(position.currentPrice)} />
              </dl>
              <dl>
                <DefinitionRow label="Target" value={formatUsd(position.targetPrice)} />
                <DefinitionRow label="Stop" value={formatUsd(position.stopPrice)} />
                <DefinitionRow label="Estimated net" value={formatSignedUsd(position.estimatedNetPnlUsd)} emphasis />
              </dl>
              <p className="border-t border-border py-4 text-sm leading-6 text-muted-foreground sm:col-span-2">{position.reason}</p>
            </div>
          ) : (
            <EmptyState
              title="Your funds are not currently in an ETH/USD position."
              description="Hyper Profits is watching the fee-adjusted setup. Waiting is an active strategy decision, not an error."
              action={<Button variant="outline" asChild><Link to="/trade">Review signal checks</Link></Button>}
            />
          )}
        </section>

        <section aria-labelledby="signals-heading">
          <div className="mb-4">
            <p className="text-xs font-bold text-muted-foreground">DECISION ENGINE</p>
            <h2 id="signals-heading" className="font-display mt-1 text-xl font-bold tracking-[-0.03em]">What the bot sees</h2>
          </div>
          <SignalList signals={signals} />
        </section>
      </div>

      <section className="border-t border-border pt-7" aria-labelledby="recent-heading">
        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold text-muted-foreground">RECENT ACTIVITY</p>
            <h2 id="recent-heading" className="font-display mt-1 text-xl font-bold tracking-[-0.03em]">Latest system events</h2>
          </div>
          <Button variant="ghost" size="sm" asChild><Link to="/activity">View all <ArrowRight data-icon="inline-end" /></Link></Button>
        </div>
        {recentEvents.length ? (
          <ul className="m-0 grid list-none p-0">
            {recentEvents.slice(0, 4).map((event) => (
              <li key={event.id} className="grid gap-1 border-b border-border py-4 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-5">
                <div className="flex items-start gap-3">
                  <span className={cn("mt-1.5 size-2 shrink-0 rounded-full bg-muted-foreground", event.severity === "success" && "bg-positive", event.severity === "warning" && "bg-coral", event.severity === "error" && "bg-destructive")} />
                  <div><p className="text-sm font-semibold">{event.title}</p><p className="mt-1 text-xs leading-5 text-muted-foreground">{event.detail}</p></div>
                </div>
                <time className="pl-5 text-xs text-muted-foreground sm:pl-0" dateTime={event.createdAt}>{formatDateTime(event.createdAt)}</time>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-7 text-sm text-muted-foreground">No system events are available yet.</p>
        )}
      </section>

      <LiveActivationDialog open={liveDialogOpen} onOpenChange={setLiveDialogOpen} onConfirmed={activateLive} />
    </div>
  );
}
